'use client';

import { cn } from '@/lib/utils';
import { getStaticUiCopy } from '@/i18n/static-ui-copy';
import { Play } from 'lucide-react';
import { useState, useRef, useEffect, useCallback } from 'react';
import { useLocale } from 'next-intl';

// Constants for autoplay retry logic
const MAX_RETRY_ATTEMPTS = 3;
const SAFARI_INITIAL_DELAY_MS = 200;
const SAFARI_RETRY_DELAY_MS = 300;
const SAFARI_METADATA_DELAY_MS = 100;
const INTERSECTION_OBSERVER_THRESHOLD = 0.1;

interface VideoViewerProps {
  src: string | null | undefined;
  className?: string;
  // Extra classes applied directly to the underlying <video> element
  videoClassName?: string;
  controls?: boolean;
  autoPlay?: boolean;
  loop?: boolean;
  muted?: boolean;
  onReady?: () => void;
  onLoadingChange?: (loading: boolean) => void;
}

export function VideoViewer({
  src,
  className,
  videoClassName,
  controls = true,
  autoPlay = false,
  loop = false,
  muted = true,
  onReady,
  onLoadingChange,
}: VideoViewerProps) {
  const locale = useLocale();
  const ui = getStaticUiCopy(locale);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [urlAccessible] = useState<boolean | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const playAttemptedRef = useRef(false);

  // Detect Safari browser
  const isSafari =
    typeof window !== 'undefined' &&
    /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  // Some callers (e.g. thumbnail rows under chat input) want the <video> to fill
  // a fixed square and use object-cover. Our default is object-contain + intrinsic sizing.
  const shouldFillContainer =
    Boolean(videoClassName?.includes('object-cover')) ||
    Boolean(videoClassName?.includes('h-full')) ||
    Boolean(videoClassName?.includes('w-full')) ||
    Boolean(videoClassName?.includes('absolute')) ||
    Boolean(videoClassName?.includes('inset-0'));

  // Safari-specific play function with retry logic
  const attemptPlay = useCallback(
    (video: HTMLVideoElement, retries = MAX_RETRY_ATTEMPTS): Promise<void> => {
      return new Promise((resolve, reject) => {
        if (retries === 0) {
          reject(new Error('Max retries reached'));
          return;
        }

        // Ensure video is muted for autoplay (Safari requirement)
        video.muted = true;

        // For Safari, wait a bit longer before attempting to play
        const delay = isSafari ? SAFARI_INITIAL_DELAY_MS : 0;

        setTimeout(() => {
          const playPromise = video.play();
          if (playPromise !== undefined) {
            playPromise
              .then(() => {
                setIsPlaying(true);
                resolve();
              })
              .catch((error) => {
                // For Safari, wait a bit and retry if needed
                if (isSafari && retries > 0) {
                  setTimeout(() => {
                    attemptPlay(video, retries - 1)
                      .then(resolve)
                      .catch(reject);
                  }, SAFARI_RETRY_DELAY_MS);
                } else {
                  console.log('Video autoplay prevented:', error);
                  reject(error);
                }
              });
          } else {
            resolve();
          }
        }, delay);
      });
    },
    [isSafari]
  );

  // Set Safari-specific attributes via ref (required for Safari compatibility)
  useEffect(() => {
    const video = videoRef.current;
    // Reset play attempt flag when src changes
    playAttemptedRef.current = false;
    setIsPlaying(false);

    if (video && autoPlay) {
      // Safari requires webkit-playsinline attribute for inline playback
      video.setAttribute('webkit-playsinline', 'true');
      video.setAttribute('x-webkit-airplay', 'allow');

      // For Safari, use IntersectionObserver to play when video enters viewport
      if (isSafari) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (
                entry.isIntersecting &&
                video.readyState >= 2 &&
                !playAttemptedRef.current
              ) {
                playAttemptedRef.current = true;
                attemptPlay(video).catch(() => {
                  // Will retry in other event handlers
                });
              }
            });
          },
          { threshold: INTERSECTION_OBSERVER_THRESHOLD }
        );

        observer.observe(video);

        const handleLoadedMetadata = () => {
          // Wait a bit for Safari to fully process metadata
          setTimeout(() => {
            if (!playAttemptedRef.current && video.readyState >= 2) {
              playAttemptedRef.current = true;
              attemptPlay(video).catch(() => {
                // Will retry in handleCanPlayThrough
              });
            }
          }, SAFARI_METADATA_DELAY_MS);
        };

        // Also try on canplay event (when video can start playing)
        const handleCanPlayNative = () => {
          if (!playAttemptedRef.current && video.readyState >= 2) {
            playAttemptedRef.current = true;
            attemptPlay(video).catch(() => {
              // Will retry in handleCanPlayThrough
            });
          }
        };

        // Try on canplaythrough (when video has enough data to play through)
        const handleCanPlayThroughNative = () => {
          if (!playAttemptedRef.current && video.readyState >= 3) {
            playAttemptedRef.current = true;
            attemptPlay(video).catch(() => {
              // Log but don't show error
            });
          }
        };

        video.addEventListener('loadedmetadata', handleLoadedMetadata);
        video.addEventListener('canplay', handleCanPlayNative);
        video.addEventListener('canplaythrough', handleCanPlayThroughNative);

        return () => {
          observer.disconnect();
          video.removeEventListener('loadedmetadata', handleLoadedMetadata);
          video.removeEventListener('canplay', handleCanPlayNative);
          video.removeEventListener(
            'canplaythrough',
            handleCanPlayThroughNative
          );
        };
      } else {
        // For other browsers, try playing immediately
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              setIsPlaying(true);
            })
            .catch((error) => {
              console.log('Video autoplay prevented:', error);
            });
        }
        return undefined;
      }
    } else if (video) {
      // Set Safari attributes even if not autoplay
      video.setAttribute('webkit-playsinline', 'true');
      video.setAttribute('x-webkit-airplay', 'allow');
      return undefined;
    }
    return undefined;
  }, [src, autoPlay, isSafari, attemptPlay]);

  const handleLoadStart = () => {
    setIsLoading(true);
    setError(null);
    onLoadingChange?.(true);
  };

  const handleCanPlay = () => {
    setIsLoading(false);
    onLoadingChange?.(false);
    // Ensure video plays when autoplay is enabled
    if (autoPlay && videoRef.current && !playAttemptedRef.current) {
      playAttemptedRef.current = true;
      attemptPlay(videoRef.current).catch(() => {
        // Will retry in handleCanPlayThrough or handleLoadedData
      });
    }
    onReady?.();
  };

  const handleLoadedData = () => {
    // Safari sometimes uses loadeddata instead of canplay
    setIsLoading(false);
    onLoadingChange?.(false);
    // Simplified: combine Safari and non-Safari logic into a single if block
    if (autoPlay && videoRef.current && !playAttemptedRef.current) {
      playAttemptedRef.current = true;
      attemptPlay(videoRef.current).catch(() => {
        if (isSafari) {
          // Log but don't show error
          console.log('Safari autoplay failed, will retry on canplaythrough');
        }
        // For other browsers, the original code had an empty catch block.
      });
    }
    onReady?.();
  };

  const handleCanPlayThrough = () => {
    // Simplified: remove redundant checks since both branches do the same thing
    if (
      autoPlay &&
      videoRef.current &&
      isSafari &&
      !isPlaying &&
      !playAttemptedRef.current
    ) {
      playAttemptedRef.current = true;
      attemptPlay(videoRef.current).catch(() => {
        // Log but don't show error - video might play on user interaction
      });
    }
  };

  const handlePlaying = () => {
    setIsPlaying(true);
  };

  const handlePause = () => {
    setIsPlaying(false);
  };

  const handleError = (e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
    const videoElement = e.currentTarget;
    const error = videoElement.error;
    let errorMessage: string = ui.failedToLoadVideo;

    if (error) {
      switch (error.code) {
        case error.MEDIA_ERR_ABORTED:
          errorMessage = ui.videoLoadingAborted;
          break;
        case error.MEDIA_ERR_NETWORK:
          errorMessage = ui.networkErrorLoadingVideo;
          break;
        case error.MEDIA_ERR_DECODE:
          errorMessage = ui.videoDecodingError;
          break;
        case error.MEDIA_ERR_SRC_NOT_SUPPORTED:
          errorMessage = ui.videoFormatNotSupported;
          break;
      }
    }

    console.error('🎥 Video error:', e, errorMessage);
    setIsLoading(false);
    setError(errorMessage);
  };

  // Handle null/undefined src
  if (!src) {
    return (
      <div
        className={cn(
          'text-muted-foreground flex items-center justify-center p-4',
          className
        )}
      >
        <p>{ui.noVideoSourceAvailable}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={cn(
          'flex items-center justify-center p-4 text-red-500',
          className
        )}
      >
        <p>{error}</p>
        <p className="mt-2 text-xs">
          {ui.source}: {src}
        </p>
        {urlAccessible !== null && (
          <p className="mt-1 text-xs">
            {ui.urlAccessible}: {urlAccessible ? ui.yes : ui.no}
          </p>
        )}
      </div>
    );
  }

  return (
    <>
      {/* Hide native play button overlay when autoplay is enabled */}
      {autoPlay && (
        <style
          dangerouslySetInnerHTML={{
            __html: `
            video[data-autoplay="true"]::-webkit-media-controls-play-button,
            video[data-autoplay="true"]::-webkit-media-controls-start-playback-button,
            video[data-autoplay="true"]::-webkit-media-controls-overlay-play-button,
            video[data-autoplay="true"]::-webkit-media-controls-overlay-enclosure {
              display: none !important;
              opacity: 0 !important;
              pointer-events: none !important;
              visibility: hidden !important;
            }
            video[data-autoplay="true"]::before {
              display: none !important;
            }
          `,
          }}
        />
      )}
      <div className={cn('relative', className)}>
        {isLoading && (
          <div className="bg-muted absolute inset-0 z-10 flex items-center justify-center">
            <div className="text-muted-foreground text-sm">
              {ui.loadingVideo}
            </div>
          </div>
        )}

        {/* Fallback display when video is not playing - only show briefly during loading */}
        {isLoading && urlAccessible === false && (
          <div className="bg-muted absolute inset-0 z-20 flex flex-col items-center justify-center p-4">
            <div className="text-muted-foreground text-center">
              <div className="mb-2">
                <Play className="mx-auto h-8 w-8" />
              </div>
              <p className="text-sm font-medium">{ui.videoAsset}</p>
              <p className="text-muted-foreground mt-1 text-xs">
                {ui.urlNotAccessible}
              </p>
              {src && (
                <p className="text-muted-foreground mt-2 text-xs break-all">
                  {ui.source}: {src.substring(0, 50)}...
                </p>
              )}
            </div>
          </div>
        )}

        <video
          ref={videoRef}
          src={src}
          controls={controls}
          autoPlay={autoPlay}
          loop={loop}
          muted={muted}
          data-autoplay={autoPlay ? 'true' : undefined}
          // By default, contain the video within its container and preserve aspect.
          // Some fixed-size thumbnail UIs pass `videoClassName` that requests fill+cover.
          className={cn(
            'rounded-md',
            shouldFillContainer
              ? 'h-full w-full object-cover'
              : 'h-auto max-h-full w-auto max-w-full object-contain',
            videoClassName
          )}
          onLoadStart={handleLoadStart}
          onCanPlay={handleCanPlay}
          onCanPlayThrough={handleCanPlayThrough}
          onLoadedData={handleLoadedData}
          onPlaying={handlePlaying}
          onPause={handlePause}
          onError={handleError}
          preload={autoPlay ? 'auto' : 'metadata'}
          playsInline
          // Ensure video has proper dimensions for Safari.
          // If we're filling a fixed container, let the element be 100% tall too.
          style={
            shouldFillContainer
              ? { width: '100%', height: '100%' }
              : { width: '100%', height: 'auto' }
          }
          // Remove crossOrigin for Safari compatibility - Safari is stricter about CORS
          // If CORS is needed, ensure the server sends proper CORS headers
        />
      </div>
    </>
  );
}
